import React, { useState, useRef, useEffect } from "react";
import {
  BookOpenText,
  MessageCircleHeart,
  ListChecks,
  Sparkles,
  Send,
  Loader2,
  ArrowRight,
  BookmarkPlus,
  NotebookPen,
  Compass,
} from "lucide-react";

/**
 * Renders the Feb-2026 5-section adaptive guidance response with a
 * prominent follow-up textbox and a contextual continuation action bar.
 *
 *   1. What I'm Hearing         (result.what_im_hearing || result.feeling)
 *   2. Wisdom for This Moment   (result.wisdom_for_moment || result.biblical_perspective)
 *   3. Let's Work Through This  (result.work_through[])
 *   4. Your Next Step           (result.next_step)
 *   5. Keep Going               (result.follow_up_question + reply textbox)
 *
 * Falls back to legacy fields when the new ones are missing so existing
 * saved history renders too.
 *
 * Props:
 *   result:         GuidanceOut
 *   onFollowUp:     async (text) => void            (submit the follow-up)
 *   onAction:       (actionId) => void              (continuation bar click)
 *   busy:           boolean                          (loading follow-up)
 *   testidPrefix:   string                           (dom test id prefix)
 */
export default function GuidanceResponse({
  result,
  onFollowUp,
  onAction,
  busy = false,
  testidPrefix = "guidance",
}) {
  const [followText, setFollowText] = useState("");
  const followRef = useRef(null);
  const rootRef = useRef(null);

  // Auto-focus the follow-up textbox once the response renders — the user
  // shouldn't have to hunt for where to reply.
  useEffect(() => {
    if (result && followRef.current) {
      // Focus on next tick so smooth-scroll can finish first.
      const t = setTimeout(() => {
        try {
          followRef.current?.focus({ preventScroll: true });
        } catch {
          /* noop */
        }
      }, 350);
      return () => clearTimeout(t);
    }
  }, [result?.id]);

  if (!result) return null;

  const whatIm = result.what_im_hearing || result.feeling;
  const wisdom = result.wisdom_for_moment || result.biblical_perspective;
  const workThrough = Array.isArray(result.work_through) ? result.work_through : [];
  const nextStep = result.next_step;
  const followUp = result.follow_up_question;
  const scripture = result.scripture_reference;
  const actions = Array.isArray(result.suggested_actions) ? result.suggested_actions : [];

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = followText.trim();
    if (!text || busy) return;
    try {
      await onFollowUp?.(text);
      setFollowText("");
    } catch {
      /* the caller is responsible for surfacing errors */
    }
  };

  return (
    <div
      ref={rootRef}
      data-testid={`${testidPrefix}-response`}
      className="rounded-3xl bg-white border border-[#4F6F52]/25 shadow-[0_6px_28px_rgba(79,111,82,0.08)] overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 sm:px-8 pt-6 pb-4 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#4F6F52] font-semibold border-b border-[#DCE5DB]/70">
        <BookOpenText size={13} />
        LifeGuide's response
      </div>

      <div className="px-6 sm:px-8 py-6 space-y-6">
        {/* 1. What I'm Hearing */}
        {whatIm && (
          <Section
            icon={<MessageCircleHeart size={13} />}
            eyebrow="What I'm hearing"
            testid={`${testidPrefix}-what-im-hearing`}
          >
            <p className="text-[15px] leading-relaxed text-[#2E2E2E]">{whatIm}</p>
          </Section>
        )}

        {/* 2. Wisdom for This Moment */}
        {wisdom && (
          <Section
            icon={<BookOpenText size={13} />}
            eyebrow="Wisdom for this moment"
            testid={`${testidPrefix}-wisdom`}
            tone="sage"
          >
            <p className="text-[15px] leading-[1.85] italic text-[#2E2E2E]">{wisdom}</p>
            {scripture && (
              <p className="mt-2 text-sm font-medium text-[#4F6F52]">— {scripture}</p>
            )}
          </Section>
        )}

        {/* 3. Let's Work Through This */}
        {workThrough.length > 0 && (
          <Section
            icon={<ListChecks size={13} />}
            eyebrow="Let's work through this"
            testid={`${testidPrefix}-work-through`}
          >
            <ul className="space-y-2.5">
              {workThrough.map((q, i) => (
                <li key={i} className="flex gap-2.5 items-start text-[15px] leading-relaxed text-[#2E2E2E]">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#4F6F52] shrink-0" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* 4. Your Next Step */}
        {nextStep && (
          <Section
            icon={<Compass size={13} />}
            eyebrow="Your next step"
            testid={`${testidPrefix}-next-step`}
            tone="cream"
          >
            <p className="text-[15px] leading-relaxed text-[#2E2E2E]">{nextStep}</p>
          </Section>
        )}

        {result.better_response && !result.say_it_better_prefill?.draft && (
          <div className="rounded-2xl bg-[#EEF3EC] border border-[#DCE5DB] px-4 py-3.5">
            <div className="text-xs uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-1.5">
              A better way to respond
            </div>
            <p className="text-[15px] text-[#2E2E2E] italic leading-relaxed">
              "{result.better_response}"
            </p>
          </div>
        )}
      </div>

      {/* 5. Keep Going — follow-up question + reply box (always shown when we
          have a follow-up so the response is never a dead-end). */}
      {followUp && onFollowUp && (
        <form
          onSubmit={submit}
          className="border-t border-[#DCE5DB]/70 bg-[#F9FAF7] px-6 sm:px-8 py-6"
          data-testid={`${testidPrefix}-follow-up`}
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#4F6F52] font-semibold mb-2">
            <Sparkles size={13} />
            Keep going
          </div>
          <p className="text-[15px] leading-relaxed text-[#2E2E2E] mb-4">
            {followUp}
          </p>
          <div className="flex items-end gap-2">
            <textarea
              ref={followRef}
              value={followText}
              onChange={(e) => setFollowText(e.target.value)}
              rows={2}
              maxLength={1000}
              disabled={busy}
              placeholder="Answer here to keep the conversation going…"
              className="flex-1 bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition text-[15px] leading-relaxed resize-none disabled:opacity-60"
              data-testid={`${testidPrefix}-follow-up-input`}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit(e);
              }}
            />
            <button
              type="submit"
              disabled={busy || followText.trim().length < 2}
              data-testid={`${testidPrefix}-follow-up-submit`}
              className="inline-flex items-center justify-center bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-50 text-white rounded-full w-11 h-11 shrink-0 transition shadow-[0_4px_14px_rgba(79,111,82,0.22)]"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2} />}
            </button>
          </div>
          <p className="text-[11px] text-[#8C9A8B] mt-2">
            LifeGuide remembers this thread while you keep going.
          </p>
        </form>
      )}

      {/* Continuation actions */}
      {actions.length > 0 && onAction && (
        <div
          className="border-t border-[#DCE5DB]/70 bg-white px-6 sm:px-8 py-4 flex flex-wrap gap-2"
          data-testid={`${testidPrefix}-actions`}
        >
          {actions.includes("say_it_better") && (
            <ActionBtn
              icon={<Sparkles size={13} />}
              label="Help me Say It Better"
              onClick={() => onAction("say_it_better")}
              testid={`${testidPrefix}-action-say-it-better`}
              primary
            />
          )}
          {actions.includes("continue_talking") && (
            <ActionBtn
              icon={<MessageCircleHeart size={13} />}
              label="Continue talking"
              onClick={() => onAction("continue_talking")}
              testid={`${testidPrefix}-action-continue-talking`}
            />
          )}
          {actions.includes("make_plan") && (
            <ActionBtn
              icon={<ListChecks size={13} />}
              label="Help me make a plan"
              onClick={() => onAction("make_plan")}
              testid={`${testidPrefix}-action-make-plan`}
            />
          )}
          {actions.includes("journal_this") && (
            <ActionBtn
              icon={<NotebookPen size={13} />}
              label="Journal this"
              onClick={() => onAction("journal_this")}
              testid={`${testidPrefix}-action-journal-this`}
            />
          )}
          {actions.includes("save_wisdom") && (
            <ActionBtn
              icon={<BookmarkPlus size={13} />}
              label="Save this wisdom"
              onClick={() => onAction("save_wisdom")}
              testid={`${testidPrefix}-action-save-wisdom`}
            />
          )}
        </div>
      )}

      {result.escalation_note && (
        <div className="px-6 sm:px-8 pb-6 pt-1">
          <p className="text-xs text-[#8C7A4F] leading-relaxed">
            {result.escalation_note}
          </p>
        </div>
      )}
    </div>
  );
}

function Section({ icon, eyebrow, children, testid, tone = "white" }) {
  const toneCls =
    tone === "sage"
      ? ""
      : tone === "cream"
      ? "rounded-2xl bg-[#F5EBD9]/40 border border-[#D6C7A1]/30 px-4 py-3.5"
      : "";
  return (
    <div data-testid={testid} className={toneCls || undefined}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#8C9A8B] font-semibold mb-2">
        <span className="text-[#4F6F52]">{icon}</span>
        {eyebrow}
      </div>
      {children}
    </div>
  );
}

function ActionBtn({ icon, label, onClick, testid, primary = false }) {
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition";
  const cls = primary
    ? `${base} bg-[#4F6F52] hover:bg-[#3F5A42] text-white shadow-[0_4px_14px_rgba(79,111,82,0.22)]`
    : `${base} bg-[#EEF3EC] hover:bg-[#DDE7DA] text-[#3F5A42] border border-[#DCE5DB]`;
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={cls}>
      {icon}
      {label}
      {primary && <ArrowRight size={12} />}
    </button>
  );
}
